import { cookies, getDstAppCookie } from "@derstandard/cookies";
import { isValidUrl } from "@derstandard/utils";
import Cookie from "js-cookie";
import type DstBiPixel from "../types/bi.d.ts";

const MediaType: DstBiPixel.MediaTypes = {
	android: 8,
	huawei: 16,
	ios: 5,
	mobile: 2,
	web: 1,
};

export class BiPixel {
	#pixelUrl: string;
	#receiverUrl: string;
	#environment: string;
	#didSendPageImpression = false;

	constructor(userOptions: DstBiPixel.Configuration) {
		const options = {
			initialize: false,
			...userOptions,
		};

		if (!options?.pixelUrl?.length || !isValidUrl(options.pixelUrl)) {
			throw new TypeError("Missing or invalid Pixel URL provided");
		}

		if (!options?.receiverUrl?.length || !isValidUrl(options.receiverUrl)) {
			throw new TypeError("Missing or invalid Receiver URL provided");
		}

		this.#pixelUrl = options.pixelUrl;
		this.#receiverUrl = options.receiverUrl;
		this.#environment = options.environment || process.env.NODE_ENV;

		if (options.initialize) {
			this.init();
		}
	}

	/**
	 * Initializes BI pixel
	 */
	init(): void {
		if (window.Pixel) {
			window.Pixel.init(this.#pixelUrl, this.#receiverUrl, {
				av: true,
				debuglog: this.#environment === "development",
				vizoverlay: this.#environment === "development",
			});
		} else {
			console.warn("Pixel object is not attached to window");
		}
	}
	/**
	 * Returns the mediatype by evaluating the app cookie
	 * @returns {DstBiPixel.MediaValues}
	 */
	#getMediaType(): DstBiPixel.MediaValues {
		const appCookie = getDstAppCookie();

		switch (true) {
			case appCookie?.os === "ios":
				return MediaType.ios;

			// Huawei also is detected as "android", so to differentiate this check needs to run first
			case appCookie?.labels?.includes("huawei"):
				return MediaType.huawei;

			case appCookie?.os === "android":
				return MediaType.android;

			default:
				return MediaType.web;
		}
	}

	/**
	 * Checks whether the `s_optout` cookie exists
	 * Checks whether the user has a `pur` entitlement
	 * @returns {boolean}
	 */
	#getNoTracking(): boolean {
		const sOptout = Boolean(Cookie.get(cookies.notTracking));
		const isPur = Boolean(
			this.#getIdTokenPayload()?.["cognito:groups"]?.includes("pur"),
		);

		return sOptout || isPur;
	}

	/**
	 * Retreives the value of the `Cognito idToken` cookie if it exists
	 * @returns {string | null}
	 */
	#getIdTokenCookie(): string | null {
		const cookies = Cookie.get();
		const idTokenCookie = Object.keys(cookies).find(
			(key) =>
				key.startsWith("CognitoIdentityServiceProvider") &&
				key.endsWith("idToken"),
		);

		if (!idTokenCookie) {
			return null;
		}

		return cookies[idTokenCookie];
	}

	/**
	 * Note that this method does not validate the idToken, it should only be used for tracking purposes.
	 * Decodes and returns the payload of the `Cognito idToken` cookie
	 * @returns {IdTokenPayload | null}
	 */
	#getIdTokenPayload(): DstBiPixel.IdTokenPayload | null {
		const idTokenCookie = this.#getIdTokenCookie();

		if (!idTokenCookie) {
			return null;
		}

		try {
			const idTokenPayload = idTokenCookie.split(".").at(1);
			const decodedPayload = JSON.parse(atob(idTokenPayload));
			return decodedPayload;
		} catch (e) {
			console.error("Error decoding idToken payload", e);
			return null;
		}
	}

	/**
	 * Records Page Impression.
	 * A CommunityAction counts as a page impression with the only difference in the action parameter.
	 *
	 * @param {DstBiPixel.PageImpressionOptions} options
	 * @return {void}
	 */
	public sendPageImpression(options: DstBiPixel.PageImpressionOptions): void {
		if (!options.correlationid?.length) {
			throw new TypeError("Missing correlation ID");
		}

		const payload: DstBiPixel.PageImpressionOptions = {
			// mandatory
			correlationid: options.correlationid,
			mediatype: this.#getMediaType(),
			notracking: options?.notracking || this.#getNoTracking(),

			// optional
			action: options?.action || undefined,
			breakpoint: window.innerWidth < 960 ? "small" : "large",
			uguid: options?.uguid || this.#getIdTokenPayload()?.userid || undefined,

			// overrides
			...options,
		};

		window.BI.send("event", "pi", payload);

		this.#didSendPageImpression = true;
	}

	/**
	 * Send additional tracking data. Usually called after a page impression, e.g. to send additional data that's loaded asynchronously.
	 * @deprecated
	 * @param {DstBiPixel.ClientDataOptions} options
	 * @return {void}
	 */
	public sendClientData(options: DstBiPixel.ClientDataOptions): void {
		if (!this.#didSendPageImpression) {
			console.warn("Page impression was not sent before sending client data");
		}

		const payload: DstBiPixel.ClientDataOptions = {
			notracking: options?.notracking || this.#getNoTracking(),
			userdata: options?.userdata?.userToken || undefined,
			...options,
		};

		window.BI.send("event", "clientdata", payload);
	}
}
